import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { WorkItem } from "@/utils/videoUtils";

interface HeroCarouselProps {
  items: WorkItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[index];

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + items.length) % items.length);
  };

  return (
    <section className="relative w-full h-[460px] md:h-[600px] overflow-hidden bg-primary-950">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 主视觉`}
            title={`${item.title} - 日本伦理剧`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>

      <div className="relative z-10 w-full h-full px-4 md:px-6 flex items-end md:items-center">
        <div className="w-full pb-16 md:pb-0 max-w-2xl">
          <div className="animate-fade-up" key={current.id}>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-label bg-accent-500 text-accent-950">
              <i className="ri-fire-line text-xs"></i>
              精选推荐
            </span>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
              {current.title}
            </h2>
            <p className="mt-2 text-sm text-background-200/90 font-label">
              {current.originalTitle} · {current.year} · {current.type} · {current.genre}
              {current.rating !== null && ` · 评分 ${current.rating.toFixed(1)}`}
            </p>
            <p className="mt-4 text-sm md:text-base text-background-100/90 leading-relaxed max-w-xl line-clamp-3">
              {current.synopsis}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-accent-950 font-label text-sm font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                查看详情
              </Link>
              <Link
                to={`/category/${current.categories[0]}`}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-background-50/15 text-background-50 font-label text-sm border border-background-50/30 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多同类作品
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="上一个"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 border border-background-50/30 hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一个"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-background-50/15 text-background-50 border border-background-50/30 hover:bg-background-50/30 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? "w-7 bg-accent-500" : "w-2 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}